import type { BookmarkId, Camera, CameraBookmark, Command, WorkspaceDocument } from '@/types';
import { asBookmarkId } from '@/types';
import { createCommand } from '@/core/commands/createCommand';
import { createId } from '@/utils/id';

type Doc = WorkspaceDocument;

const withBookmarks = (d: Doc, bookmarks: readonly CameraBookmark[]): Doc => ({
  ...d,
  workspace: { ...d.workspace, bookmarks },
});

export const addBookmarkCommand = (
  doc: Doc,
  camera: Camera,
  name: string,
  now = Date.now(),
  id: BookmarkId = asBookmarkId(createId('bm')),
): Command<Doc> => {
  const bookmark: CameraBookmark = {
    id,
    name: name.trim() || `View ${doc.workspace.bookmarks.length + 1}`,
    camera,
    createdAt: now,
  };
  return createCommand<Doc>({
    name: 'bookmark.add',
    label: `Bookmark "${bookmark.name}"`,
    execute: (d) => withBookmarks(d, [...d.workspace.bookmarks, bookmark]),
    undo: (d) =>
      withBookmarks(
        d,
        d.workspace.bookmarks.filter((b) => b.id !== id),
      ),
  });
};

export const removeBookmarkCommand = (doc: Doc, id: BookmarkId): Command<Doc> | null => {
  const index = doc.workspace.bookmarks.findIndex((b) => b.id === id);
  const removed = doc.workspace.bookmarks[index];
  if (!removed) return null;
  return createCommand<Doc>({
    name: 'bookmark.remove',
    label: `Remove bookmark "${removed.name}"`,
    execute: (d) =>
      withBookmarks(
        d,
        d.workspace.bookmarks.filter((b) => b.id !== id),
      ),
    undo: (d) =>
      withBookmarks(d, [...d.workspace.bookmarks.slice(0, index), removed, ...d.workspace.bookmarks.slice(index)]),
  });
};

export const renameBookmarkCommand = (doc: Doc, id: BookmarkId, name: string): Command<Doc> | null => {
  const before = doc.workspace.bookmarks.find((b) => b.id === id);
  const next = name.trim();
  if (!before || !next || before.name === next) return null;
  const rename = (d: Doc, value: string) =>
    withBookmarks(
      d,
      d.workspace.bookmarks.map((b) => (b.id === id ? { ...b, name: value } : b)),
    );
  return createCommand<Doc>({
    name: 'bookmark.rename',
    label: 'Rename bookmark',
    execute: (d) => rename(d, next),
    undo: (d) => rename(d, before.name),
  });
};
